import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import XButton from "@streeteasy/design-system/deprecated/XButton";
import { bool, func, number, shape, string } from "prop-types";

import AreaSelectorModal from "../../../maps/components/AreaSelector/AreaSelectorModal/AreaSelectorModal";
import { locations } from "../../propTypes";
import analyticsService from "../../services/analyticsService";
import LocationSelector from "../LocationSelector";
import { useFocusOnce } from "./hooks";
import * as S from "./styled";

const LocationFilterModal = ({
  areaOptions,
  dimensions,
  initialSelectedAreas,
  isOpen,
  onApply,
  onClose,
  rootElementId
}) => {
  const modalTitleElementId = "modalTitleId";
  const [isAreasModalOpen, setAreasModalOpen] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState(initialSelectedAreas);
  const [searchInputRef, setSearchInputRef] = useFocusOnce();

  const openAreaSelectorModal = () => {
    analyticsService.sendViewMapEvent();
    setAreasModalOpen(true);
    onClose();
  };

  const closeAreaSelectorModal = () => {
    setAreasModalOpen(false);
  };

  const areasMapModalSelectionHandler = ids => {
    const filteredIds = ids.filter(
      id => !ids.includes(areaOptions.find(area => area.id === id).parent_id)
    );

    closeAreaSelectorModal();
    onApply(areaOptions.filter(({ id }) => filteredIds.includes(id)));
  };

  // Safari has an issue, so it scrolls to the bottom when
  // the modal is closed with ESC and text input is in focus.
  // When we "updating" the field the issue does not reproduce.
  useEffect(() => {
    if (searchInputRef.current) {
      const prevValue = searchInputRef.current.value;
      searchInputRef.current.value = `${prevValue} `;
      searchInputRef.current.value = prevValue;
    }
  }, [searchInputRef, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedAreas(initialSelectedAreas);
      analyticsService.sendOpenModalEvent(
        initialSelectedAreas.map(({ id }) => id)
      );
    }
  }, [isOpen]);

  const resetHandler = () => {
    analyticsService.sendResetEvent(selectedAreas.map(({ id }) => id));
    setSelectedAreas([]);
  };

  const applyHandler = () => {
    analyticsService.sendDoneEvent(selectedAreas.map(({ id }) => id));
    onApply(selectedAreas);
  };

  const closeButtonClickHandler = () => {
    analyticsService.sendCloseButtonClickEvent(
      selectedAreas.map(({ id }) => id)
    );
    onClose();
  };

  const overlayClickHandler = () => {
    analyticsService.sendOverlayClickEvent(selectedAreas.map(({ id }) => id));
    onApply(selectedAreas);
  };

  return (
    <>
      <S.Modal
        aria={{
          labelledby: modalTitleElementId
        }}
        dimensions={dimensions}
        isOpen={isOpen}
        onRequestClose={overlayClickHandler}
        rootElementId={rootElementId}
      >
        <S.ModalWrapper>
          <S.CloseButtonWrapper>
            <XButton onClick={closeButtonClickHandler} />
          </S.CloseButtonWrapper>

          <S.Content>
            <S.Title id={modalTitleElementId}>Location</S.Title>
            <LocationSelector
              ref={setSearchInputRef}
              areaOptions={areaOptions}
              initialSelectedAreas={selectedAreas}
              onSelectedAreasChange={setSelectedAreas}
            />
          </S.Content>

          <S.MapModalButton onClick={openAreaSelectorModal}>
            <S.MapModalIcon />
            See all neighborhoods
          </S.MapModalButton>

          <S.Footer>
            <S.Reset
              disabled={selectedAreas.length === 0}
              onClick={resetHandler}
            >
              Reset
            </S.Reset>
            <S.Apply onClick={applyHandler}>Done</S.Apply>
          </S.Footer>
        </S.ModalWrapper>
      </S.Modal>

      {isAreasModalOpen &&
        ReactDOM.createPortal(
          <AreaSelectorModal
            dropDownElSelector=""
            initialSelectedAreasIds={selectedAreas.map(({ id }) => id)}
            isOpen={isAreasModalOpen}
            onApply={areasMapModalSelectionHandler}
            onClose={closeAreaSelectorModal}
          />,
          global.document.body
        )}
    </>
  );
};

LocationFilterModal.propTypes = {
  dimensions: shape({
    left: number.isRequired,
    top: number.isRequired,
    width: number.isRequired
  }),
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onApply: func.isRequired,
  rootElementId: string.isRequired,
  areaOptions: locations.isRequired,
  initialSelectedAreas: locations
};

LocationFilterModal.defaultProps = {
  initialSelectedAreas: [],
  dimensions: {
    left: 40,
    top: 60,
    width: 302
  }
};

export default LocationFilterModal;
